import * as Sentry from '@sentry/react';
import { env } from './env';

type FeedbackIntegration = ReturnType<typeof Sentry.feedbackIntegration>;
type FeedbackForm = Awaited<ReturnType<FeedbackIntegration['createForm']>>;

let feedback: FeedbackIntegration | null = null;
let form: FeedbackForm | null = null;

export function initSentry() {
  if (!env.SENTRY_DSN) return;

  // autoInject is off: rather than a floating button (which collides with the
  // tawk.to chat bubble bottom-right and the sidebar bottom-left on /app/*),
  // the feedback form is opened from the sidebar "Report a bug" item via
  // openBugReport(). See src/components/layout/Sidebar.tsx.
  feedback = Sentry.feedbackIntegration({
    colorScheme: 'light',
    autoInject: false,
    showBranding: false,
    submitButtonLabel: 'Send report',
    formTitle: 'Report a bug',
  });

  Sentry.init({
    dsn: env.SENTRY_DSN,
    environment: env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      feedback,
    ],
    tracesSampleRate: env.IS_PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Open the Sentry user-feedback form. The dialog is created once and reused.
 * No-op when Sentry is not initialized (empty VITE_SENTRY_DSN).
 */
export async function openBugReport() {
  if (!feedback) return;
  if (!form) {
    form = await feedback.createForm();
    form.appendToDom();
  }
  form.open();
}

/** Whether the bug-report entry point should render (Sentry initialized). */
export const isBugReportEnabled = () => Boolean(env.SENTRY_DSN);

export { Sentry };
